import { useState } from "react";
import { HelpCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * Small "(?)" icon that pops up a help tooltip on click.
 *
 * Why Popover and not Tooltip?  Tooltip is hover-only — useless on touch devices.
 * Popover gives us tap-to-open + tap-outside-to-close, which is the correct
 * accessibility behaviour on mobile.  Trigger is a focusable <button> so
 * keyboard navigation works too.
 *
 * Props:
 *   title:    Optional bold heading inside the tip (e.g. "Target Margin")
 *   children: The body of the tip (string or JSX).
 *   side:     "top" | "right" | "bottom" | "left" (default "top")
 *   testId:   data-testid on the trigger button.
 *   className: extra classes on the trigger icon (e.g. sizing).
 */
export function HelpTip({
  title,
  children,
  side = "top",
  align = "center",
  testId,
  className,
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={title ? `Help: ${title}` : "Help"}
          data-testid={testId}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "inline-flex items-center justify-center align-middle ml-1.5",
            "h-4 w-4 rounded-full text-slate-400 hover:text-amber-400",
            "transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500/40",
            className
          )}
        >
          <HelpCircle className="h-4 w-4" strokeWidth={2} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        sideOffset={6}
        className="bg-slate-900 border-slate-700 text-slate-100 max-w-xs text-sm shadow-xl p-3"
      >
        {title && (
          <div className="text-amber-400 font-semibold mb-1.5">{title}</div>
        )}
        <div className="text-slate-300 leading-relaxed">{children}</div>
      </PopoverContent>
    </Popover>
  );
}

export default HelpTip;
